<template>
  <div class="plan-page">
    <header class="page-hero">
      <div class="nav-row">
        <button @click="$router.go(-1)">‹</button>
        <span>定投专区</span>
        <button @click="showGuide = true">规则</button>
      </div>
      <span class="hero-en">DISCIPLINED INVESTING</span>
      <h1>让长期投资，变得简单而有纪律</h1>
      <p>通过定期定额和智能策略，分散投资时点，建立适合自己的长期计划。</p>
      <div class="hero-stats">
        <div><strong>灵活</strong><span>扣款周期</span></div>
        <div><strong>透明</strong><span>计划进度</span></div>
        <div><strong>可控</strong><span>随时暂停</span></div>
      </div>
    </header>

    <main>
      <section class="smart-card">
        <div class="card-title">
          <div>
            <span>SMART STRATEGY</span>
            <h2>智能定投策略</h2>
          </div>
          <i>推荐</i>
        </div>
        <div class="strategy-list">
          <button
            v-for="strategy in strategies"
            :key="strategy.id"
            :class="{ active: selectedStrategy === strategy.id }"
            @click="selectedStrategy = strategy.id"
          >
            <strong>{{ strategy.name }}</strong>
            <small>{{ strategy.desc }}</small>
            <span>{{ selectedStrategy === strategy.id ? "✓" : "" }}</span>
          </button>
        </div>
      </section>

      <section class="setup-card">
        <div class="card-title">
          <div>
            <span>CREATE A PLAN</span>
            <h2>设置定投计划</h2>
          </div>
          <small>预计 1 分钟</small>
        </div>
        <label class="form-item">
          <span>定投金额</span>
          <div class="amount-input">
            <input v-model="amount" inputmode="decimal" placeholder="100.00" />
            <b>USD</b>
          </div>
          <small>单次最低 50 USD</small>
        </label>
        <div class="form-item">
          <span>定投周期</span>
          <div class="choice-row">
            <button
              v-for="period in periods"
              :key="period"
              :class="{ active: selectedPeriod === period }"
              @click="selectedPeriod = period"
            >
              {{ period }}
            </button>
          </div>
        </div>
        <div class="form-item">
          <span>扣款日期</span>
          <div class="choice-row date-row">
            <button
              v-for="date in dates"
              :key="date"
              :class="{ active: selectedDate === date }"
              @click="selectedDate = date"
            >
              {{ date }}
            </button>
          </div>
        </div>
        <button class="primary-btn" @click="createPlan">下一步，选择产品</button>
        <p class="form-tip">创建计划不代表自动扣款，最终方案需在确认页完成授权。</p>
      </section>

      <section class="product-section">
        <div class="card-title">
          <div>
            <span>PLAN PRODUCTS</span>
            <h2>定投产品专区</h2>
          </div>
          <button @click="$router.push('/category')">全部产品 ›</button>
        </div>
        <article v-for="product in products" :key="product.name" @click="$router.push('/category')">
          <div class="product-head">
            <span>{{ product.tag }}</span>
            <small>{{ product.risk }}</small>
          </div>
          <h3>{{ product.name }}</h3>
          <p>{{ product.desc }}</p>
          <div class="metrics">
            <div><strong>{{ product.rate }}</strong><span>业绩比较基准</span></div>
            <div><strong>{{ product.min }}</strong><span>定投起点</span></div>
            <div><strong>{{ product.period }}</strong><span>建议持有</span></div>
          </div>
        </article>
      </section>

      <section class="education-card">
        <span>定投小课堂</span>
        <h2>定投不是“稳赚”，核心是长期纪律</h2>
        <p>定投可以分散单次买入的时点风险，但不能消除市场波动。请根据自身风险承受能力、资金期限及目标选择产品。</p>
        <button @click="$router.push('/knowledge?tab=course')">学习定投基础 →</button>
      </section>
    </main>

    <div v-if="showGuide" class="modal" @click.self="showGuide = false">
      <div>
        <button class="close" @click="showGuide = false">×</button>
        <h2>定投服务说明</h2>
        <p>1. 定投计划支持按周、双周或按月执行。</p>
        <p>2. 执行日前请确保账户可用余额充足。</p>
        <p>3. 市场休市时，计划将顺延至下一可交易日。</p>
        <p>4. 用户可在计划执行前暂停或终止，已成交份额不受影响。</p>
        <p>5. 定投不承诺本金安全或固定收益。</p>
        <button class="primary-btn" @click="showGuide = false">我已了解</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "InvestmentPlan",
  data() {
    return {
      amount: "",
      selectedPeriod: "每月",
      selectedDate: "5日",
      selectedStrategy: "balanced",
      showGuide: false,
      periods: ["每周", "每两周", "每月"],
      dates: ["1日", "5日", "10日", "20日"],
      strategies: [
        { id: "fixed", name: "经典定额", desc: "每期投入固定金额，简单清晰" },
        { id: "balanced", name: "均衡智投", desc: "结合目标周期，动态提示投入节奏" },
        { id: "value", name: "价值加码", desc: "市场回撤时提示加码，适合进阶用户" }
      ],
      products: [
        { tag: "定返分红", risk: "按日返息", name: "稳享定期返利计划", desc: "按日返息至账户，产品到期后返还本金。", rate: "5.80%", min: "50 USD", period: "180天" },
        { tag: "到期结算", risk: "返本返息", name: "优选到期返本返息计划", desc: "产品周期内累计返利，到期统一结算本金与返利。", rate: "8.60%", min: "100 USD", period: "365天" },
        { tag: "灵活周期", risk: "电子合同", name: "短期按日返息计划", desc: "购买后自动生成本金及约定收益保障电子合同，可随时查看合同权益。", rate: "3.60%", min: "50 USD", period: "90天" }
      ]
    };
  },
  methods: {
    createPlan() {
      const amount = Number(this.amount);
      if (!amount || amount < 50) {
        this.$toast("请输入不低于 50 USD 的定投金额");
        return;
      }
      this.$dialog
        .confirm({
          title: "计划已生成",
          message: `${this.selectedStrategyName} · ${this.selectedPeriod}${this.selectedDate} · 每期 ${amount.toFixed(2)} USD\n下一步请选择适合您风险等级的产品。`,
          confirmButtonText: "选择产品",
          cancelButtonText: "继续调整"
        })
        .then(() => this.$router.push("/category"))
        .catch(() => {});
    }
  },
  computed: {
    selectedStrategyName() {
      const strategy = this.strategies.find(item => item.id === this.selectedStrategy);
      return strategy ? strategy.name : "定投计划";
    }
  }
};
</script>

<style scoped lang="less">
.plan-page { width: 100%; max-width: 100%; min-height: 100vh; padding-bottom: 76px; overflow-x: hidden; color: #1d2a40; background: #f3f6fa; }
.plan-page,.plan-page * { box-sizing: border-box; }
button, input { font: inherit; border: 0; background: none; }
.page-hero { padding: 15px 17px 25px; color: #fff; background: radial-gradient(circle at 90% 30%, rgba(89,176,255,.35), transparent 28%), linear-gradient(145deg,#071f4d,#0d4b93); border-radius: 0 0 28px 28px; }
.nav-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 30px; font-size: 15px; }
.nav-row button { width: 40px; color: #fff; font-size: 13px; }
.nav-row button:first-child { text-align: left; font-size: 28px; }
.hero-en, .card-title span { color: #81b7ed; font-size: 8px; font-weight: 700; letter-spacing: 1.3px; }
.page-hero h1 { max-width: 300px; margin: 8px 0; font-size: 25px; line-height: 1.35; }
.page-hero > p { max-width: 320px; color: rgba(255,255,255,.68); font-size: 10px; line-height: 1.7; }
.hero-stats { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); width: 100%; margin-top: 23px; padding: 15px; background: rgba(255,255,255,.09); border: 1px solid rgba(255,255,255,.12); border-radius: 16px; }
.hero-stats div { text-align: center; border-left: 1px solid rgba(255,255,255,.12); }
.hero-stats div:first-child { border: 0; }
.hero-stats strong, .hero-stats span { display: block; }
.hero-stats strong { font-size: 14px; }
.hero-stats span { margin-top: 4px; color: rgba(255,255,255,.55); font-size: 8px; }
main { padding: 14px; }
.smart-card,.setup-card,.product-section,.education-card { margin-bottom: 14px; padding: 17px 14px; background: #fff; border-radius: 19px; }
.card-title { display: flex; align-items: center; justify-content: space-between; margin-bottom: 15px; }
.card-title h2 { margin-top: 4px; font-size: 18px; }
.card-title i { padding: 4px 8px; color: #a36612; font-size: 9px; font-style: normal; background: #fff1d4; border-radius: 10px; }
.card-title > small,.card-title > button { color: #8b96a6; font-size: 9px; border: 0; background: none; }
.strategy-list { display: grid; gap: 8px; }
.strategy-list button { position: relative; padding: 13px 42px 13px 13px; text-align: left; border: 1px solid #e7ecf3; border-radius: 13px; }
.strategy-list button.active { border-color: #2d70c8; background: #f1f7ff; }
.strategy-list strong,.strategy-list small { display: block; }
.strategy-list strong { color: #324156; font-size: 12px; }
.strategy-list small { margin-top: 5px; color: #8e99a8; font-size: 9px; }
.strategy-list button > span { position: absolute; top: 17px; right: 15px; display: grid; width: 19px; height: 19px; place-items: center; color: #fff; font-size: 10px; background: #3478d0; border-radius: 50%; }
.form-item { display: block; margin-bottom: 18px; }
.form-item > span { display: block; margin-bottom: 9px; color: #46546a; font-size: 11px; }
.amount-input { display: flex; align-items: center; padding: 0 13px; border: 1px solid #e0e6ee; border-radius: 13px; }
.amount-input input { width: 100%; height: 48px; color: #1e385f; font-size: 20px; outline: none; }
.amount-input b { color: #456386; font-size: 11px; }
.form-item > small { display: block; margin-top: 6px; color: #a1a9b5; font-size: 8px; }
.choice-row { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 8px; }
.choice-row button { padding: 11px 4px; color: #637085; font-size: 10px; background: #f5f7fa; border: 1px solid transparent; border-radius: 10px; }
.choice-row button.active { color: #2467bf; font-weight: 700; border-color: #377ad1; background: #edf5ff; }
.date-row { grid-template-columns: repeat(4,minmax(0,1fr)); }
.primary-btn { width: 100%; padding: 14px; color: #fff; font-size: 12px; font-weight: 700; background: linear-gradient(90deg,#1555aa,#2f82dc); border-radius: 13px; box-shadow: 0 9px 18px rgba(33,104,190,.2); }
.form-tip { margin-top: 9px; color: #9ea7b3; font-size: 8px; text-align: center; }
.product-section article { padding: 14px; margin-bottom: 10px; border: 1px solid #e7ecf2; border-radius: 15px; }
.product-head { display: flex; justify-content: space-between; }
.product-head span { padding: 3px 7px; color: #2a6dc3; font-size: 8px; background: #edf4fd; border-radius: 5px; }
.product-head small { color: #939daa; font-size: 8px; }
.product-section h3 { margin: 9px 0 5px; font-size: 14px; }
.product-section article > p { color: #8490a0; font-size: 9px; line-height: 1.5; }
.metrics { display: grid; grid-template-columns: repeat(3,1fr); margin-top: 13px; }
.metrics div { padding-left: 10px; border-left: 1px solid #edf0f4; }
.metrics div:first-child { padding: 0; border: 0; }
.metrics strong,.metrics span { display: block; }
.metrics strong { color: #284b77; font-size: 13px; }
.metrics div:first-child strong { color: #d35d49; font-size: 18px; }
.metrics span { margin-top: 4px; color: #a1a9b3; font-size: 7px; }
.education-card { color: #fff; background: linear-gradient(135deg,#40346e,#66579b); }
.education-card > span { color: #c8bdf0; font-size: 9px; }
.education-card h2 { margin: 8px 0; font-size: 17px; }
.education-card p { color: rgba(255,255,255,.68); font-size: 9px; line-height: 1.7; }
.education-card button { margin-top: 12px; color: #f5dc9e; font-size: 10px; }
.modal { position: fixed; z-index: 100; inset: 0; display: flex; align-items: flex-end; background: rgba(4,14,31,.55); }
.modal > div { position: relative; width: 100%; padding: 24px 18px; background: #fff; border-radius: 24px 24px 0 0; }
.modal h2 { margin-bottom: 16px; font-size: 19px; }
.modal p { margin-bottom: 11px; color: #657186; font-size: 10px; line-height: 1.6; }
.modal .close { position: absolute; top: 15px; right: 16px; color: #8490a0; font-size: 24px; }
.modal .primary-btn { margin-top: 8px; }
</style>
